import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_2 = { class: "max-w-[1040px]" }
const _hoisted_3 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_4 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_5 = { class: "truncate" }
const _hoisted_6 = { class: "max-w-[1040px]" }
const _hoisted_7 = {
  key: 0,
  class: "mb-4 text-sm text-red-600",
  role: "alert"
}
const _hoisted_8 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_9 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_10 = { class: "truncate" }
const _hoisted_11 = {
  key: 1,
  class: "flex items-center justify-between px-2 py-3"
}
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "text-sm text-gray-500" }
const _hoisted_14 = { class: "max-w-[1040px]" }
const _hoisted_15 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_16 = { class: "flex justify-end" }
const _hoisted_17 = { class: "truncate" }
const _hoisted_18 = { class: "truncate" }

import { ChevronDownIcon, SunIcon } from '@heroicons/vue/20/solid'
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'
import { computed, onMounted, ref } from 'vue'

import TablistGrid from '@/components/neue/TablistGrid.vue'
import { useRouteTabs } from '@/composables/useRouteTabs'
import { pairComparator } from '@/helpers/conversationSort'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'

const SENT_LOAD_ERROR = "Couldn't load your sent messages. Please refresh the page."

const sentPageSize = 25
const listLimit = 10


export default {
  __name: 'MessagesNeue',
  setup(__props) {

TimeAgo.addLocale(en)
const timeAgo = new TimeAgo('en-CA')

const TABS = [
  { id: 'unread', name: 'Unread messages' },
  { id: 'sent', name: 'Sent messages' },
  { id: 'upcoming', name: 'Upcoming message automations' }
]

const { activeTab, sections } = useRouteTabs(TABS, '/messages')

const messageCols = [{ width: 'minmax(100px, 1fr)' }, { width: 'minmax(140px, 2fr)' }, { width: 'minmax(100px, 1fr)' }]

const automationCols = [
  { width: 'minmax(130px, 1fr)' },
  { width: 'minmax(80px, 1fr)' },
  { width: 'minmax(140px, 1fr)' },
  { width: '140px' }
]

const relativeFormat = x => timeAgo.format(new Date(x))
const absoluteFormat = x => new Intl.DateTimeFormat([], { dateStyle: 'short', timeStyle: 'short' }).format(new Date(x))

const modalOpen = ref(false)
const cachebuster = ref(0)
setInterval(() => cachebuster.value++, 60 * 1000)

const lastMessage = pair => {
  const detail = mapRelationship(pair.relationships.conversation_user_pair_detail.data)
  const recent = mapRelationships(detail?.relationships.most_recent_inbound_messages.data)
  return recent[0]
}

const sentRows = ref([])
const sentIncluded = ref({})
const sentPage = ref(0)
const sentHasMore = ref(false)
const sentLoading = ref(true)
const sentError = ref(false)

// Index the JSONAPI `included` array by `type:id` for local resolution
// (this section uses receive:false, so nothing lands in the Vuex store).
const buildIncludeIndex = included => {
  const idx = {}
  for (const obj of included || []) idx[`${obj.type}:${obj.id}`] = obj
  return idx
}

const sentMessage = pair => {
  const detailRef = pair.relationships?.conversation_user_pair_detail?.data
  const detail = detailRef && sentIncluded.value[`${detailRef.type}:${detailRef.id}`]
  const msgRef = detail?.relationships?.most_recent_outbound_messages?.data?.[0]
  return msgRef ? sentIncluded.value[`${msgRef.type}:${msgRef.id}`] : undefined
}

const loadSent = async () => {
  sentLoading.value = true
  sentError.value = false
  try {
    const res = await api.call(
      'GET',
      `sent_messages?limit=${sentPageSize}&page=${sentPage.value}&included=conversation_user_pair_detail,conversation_user_pair_detail.most_recent_outbound_messages`,
      {},
      { receive: false }
    )
    sentRows.value = res.data.data || []
    sentIncluded.value = buildIncludeIndex(res.data.included)
    sentHasMore.value = sentRows.value.length === sentPageSize
  } catch {
    sentError.value = true
  } finally {
    sentLoading.value = false
  }
}

const sentNext = () => {
  if (sentHasMore.value) {
    sentPage.value++
    loadSent()
  }
}

const sentPrev = () => {
  if (sentPage.value > 0) {
    sentPage.value--
    loadSent()
  }
}

const pairs = computed(() => mapType('conversation_user_pair')?.sort(pairComparator('recent', 'desc')))

const showAll = ref({
  unread: false,
  automations: false
})

const unread = computed(() =>
  pairs.value
    // unread only
    ?.filter(x => x.attributes['has_unread?'])
)

const automations = computed(() =>
  [...mapType('message_automation'), ...mapType('bottle_reminder_automation')]
    .filter(x => x.attributes.status !== 'completed' && automationTime(x))
    ?.sort((a, b) => new Date(automationTime(a)) - new Date(automationTime(b)))
)

const automationTime = automation =>
  mapRelationship(automation.relationships.automation_scheduler?.data)?.attributes.datetime

const automationDrop = automation => mapRelationship(automation.relationships.bottle_cycle?.data)

const automationAudiences = automation => mapRelationships(automation.relationships.audiences?.data)

const automationLink = automation => {
  if (automation.attributes.type === 'BottleReminderAutomation') {
    return '/store/drops/' + automationDrop(automation).id
  }
  if (automation.attributes.type === 'MessageAutomation') {
    return '/automation/message/' + automation.id
  }
}

const canCreateMessages = computed(() => {
  return privilegeService.canAccessResourceAction('messages', null, 'create')
})

onMounted(() => {
  loadSent()
  api.call(
    'GET',
    'conversation_user_pairs?included=conversation_user_pair_detail,conversation_user_pair_detail.most_recent_inbound_messages'
  )
  // TODO(alicja): pagination?
  api.call('GET', 'message_automations?included=automation_scheduler')
  api.call('GET', 'bottle_reminder_automations?included=bottle_cycle,automation_scheduler,audiences')
})

return (_ctx, _cache) => {
  const _component_ConversationsMenu = _resolveComponent("ConversationsMenu")
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_NewContactModal = _resolveComponent("NewContactModal")
  const _component_TablistHeader = _resolveComponent("TablistHeader")
  const _component_router_link = _resolveComponent("router-link")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_NeueWell = _resolveComponent("NeueWell")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    sidebar: _withCtx(() => [
      _createVNode(_component_ConversationsMenu, {
        onAddContact: _cache[0] || (_cache[0] = $event => (modalOpen.value = true))
      })
    ]),
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, {
        text: "Messages",
        sections: _unref(sections)
      }, null, 8, ["sections"])
    ]),
    main: _withCtx(() => [
      _createVNode(_component_NewContactModal, {
        "is-open": modalOpen.value,
        onCloseModal: _cache[1] || (_cache[1] = $event => (modalOpen.value = false))
      }, null, 8, ["is-open"]),
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (unread.value?.length)
              ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                  head: _withCtx(() => [
                    _createVNode(_component_TablistHeader, null, {
                      default: _withCtx(() => [
                        _createVNode(TablistGrid, {
                          columns: messageCols,
                          header: ""
                        }, {
                          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                            _createElementVNode("div", null, "Customer", -1),
                            _createElementVNode("div", null, "Last message", -1),
                            _createElementVNode("div", null, "Sent time", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(showAll.value.unread ? unread.value : unread.value.slice(0, listLimit), (pair) => {
                      return (_openBlock(), _createBlock(_component_TablistRow, {
                        key: pair.id
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_router_link, {
                            class: "w-full cursor-pointer",
                            to: '/messages/' + pair.id
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, { columns: messageCols }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_4, _toDisplayString(pair.attributes.display_name), 1),
                                  _createElementVNode("div", _hoisted_5, _toDisplayString(lastMessage(pair)?.attributes.body_decoded), 1),
                                  (_openBlock(), _createElementBlock("div", {
                                    key: cachebuster.value,
                                    class: "truncate"
                                  }, _toDisplayString(relativeFormat(pair.attributes.last_sent_timestamp)), 1))
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["to"])
                        ]),
                        _: 2
                      }, 1024))
                    }), 128)),
                    (unread.value.length > listLimit && !showAll.value.unread)
                      ? (_openBlock(), _createBlock(_component_TablistRow, {
                          key: 0,
                          class: "font-bold text-gray-700 cursor-pointer",
                          onClick: _cache[2] || (_cache[2] = $event => (showAll.value.unread = true))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, [
                              _cache[5] || (_cache[5] = _createTextVNode(" Show more ", -1)),
                              _createVNode(_unref(ChevronDownIcon), { class: "h-6 w-6 inline" })
                            ])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          (!unread.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueWell, {
                key: 0,
                class: "text-center"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SunIcon), { class: "mx-auto h-12 w-12 text-gray-400" }),
                  _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-900" }, "No unread messages!", -1))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, _unref(activeTab) === 'unread']
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_6, [
          (sentError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(SENT_LOAD_ERROR)))
            : _createCommentVNode("", true),
          _createVNode(_component_NeueLoading, { loading: sentLoading.value }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, [
                (sentRows.value.length)
                  ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                      head: _withCtx(() => [
                        _createVNode(_component_TablistHeader, null, {
                          default: _withCtx(() => [
                            _createVNode(TablistGrid, {
                              columns: messageCols,
                              header: ""
                            }, {
                              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                _createElementVNode("div", null, "Customer", -1),
                                _createElementVNode("div", null, "Last message sent", -1),
                                _createElementVNode("div", null, "Sent time", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sentRows.value, (pair) => {
                          return (_openBlock(), _createBlock(_component_TablistRow, {
                            key: pair.id
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_router_link, {
                                class: "w-full cursor-pointer",
                                to: '/messages/' + pair.id
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(TablistGrid, { columns: messageCols }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_9, _toDisplayString(pair.attributes.display_name), 1),
                                      _createElementVNode("div", _hoisted_10, _toDisplayString(sentMessage(pair)?.attributes.body_decoded), 1),
                                      (_openBlock(), _createElementBlock("div", {
                                        key: cachebuster.value,
                                        class: "truncate"
                                      }, _toDisplayString(sentMessage(pair) ? relativeFormat(sentMessage(pair).attributes.created_at) : ''), 1))
                                    ]),
                                    _: 2
                                  }, 1024)
                                ]),
                                _: 2
                              }, 1032, ["to"])
                            ]),
                            _: 2
                          }, 1024))
                        }), 128))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              (!sentRows.value.length && !sentLoading.value && !sentError.value)
                ? (_openBlock(), _createBlock(_component_NeueWell, { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_EmptyState, {
                        plural: "sent messages",
                        description: ""
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["loading"]),
          (sentHasMore.value || sentPage.value > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                (sentPage.value > 0)
                  ? (_openBlock(), _createBlock(_component_NeueButton, {
                      key: 0,
                      disabled: sentLoading.value,
                      onClick: sentPrev
                    }, {
                      default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                        _createTextVNode("Previous", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled"]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_12)),
                _createElementVNode("span", _hoisted_13, "Page " + _toDisplayString(sentPage.value + 1), 1),
                _createVNode(_component_NeueButton, {
                  disabled: !sentHasMore.value || sentLoading.value,
                  onClick: sentNext
                }, {
                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                    _createTextVNode("Next", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"])
              ]))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, _unref(activeTab) === 'sent']
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            (automations.value?.length)
              ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                  head: _withCtx(() => [
                    _createVNode(_component_TablistHeader, null, {
                      default: _withCtx(() => [
                        _createVNode(TablistGrid, {
                          columns: automationCols,
                          header: "",
                          class: "items-center"
                        }, {
                          default: _withCtx(() => [
                            _cache[11] || (_cache[11] = _createElementVNode("div", null, "Scheduled time", -1)),
                            _cache[12] || (_cache[12] = _createElementVNode("div", null, "Lists", -1)),
                            _cache[13] || (_cache[13] = _createElementVNode("div", null, "Drop/automation", -1)),
                            _createElementVNode("div", _hoisted_16, [
                              (canCreateMessages.value)
                                ? (_openBlock(), _createBlock(_component_router_link, {
                                    key: 0,
                                    to: "/automation/message/new"
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_NeueButton, {
                                        type: "primary",
                                        class: "whitespace-nowrap"
                                      }, {
                                        default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                          _createTextVNode("+ Message", -1)
                                        ]))]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(showAll.value.automations ? automations.value : automations.value.slice(0, listLimit), (automation) => {
                      return (_openBlock(), _createBlock(_component_TablistRow, {
                        key: automation.type + automation.id
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_router_link, {
                            class: "w-full cursor-pointer",
                            to: automationLink(automation)
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, { columns: automationCols }, {
                                default: _withCtx(() => [
                                  (_openBlock(), _createElementBlock("div", {
                                    key: cachebuster.value,
                                    class: "text-gray-900 font-medium truncate"
                                  }, _toDisplayString(`${relativeFormat(automationTime(automation))} (${absoluteFormat(automationTime(automation))})`), 1)),
                                  _createElementVNode("div", _hoisted_17, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(automationAudiences(automation), (audience, i) => {
                                      return (_openBlock(), _createElementBlock("span", {
                                        key: audience.id
                                      }, _toDisplayString(audience.attributes.audience_name +
                          (i !== automationAudiences(automation).length - 1 ? ', ' : '')), 1))
                                    }), 128))
                                  ]),
                                  _createElementVNode("div", _hoisted_18, _toDisplayString(automationDrop(automation)?.attributes.description || automation.attributes.name), 1),
                                  _cache[14] || (_cache[14] = _createElementVNode("div", null, null, -1))
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["to"])
                        ]),
                        _: 2
                      }, 1024))
                    }), 128)),
                    (automations.value.length > listLimit && !showAll.value.automations)
                      ? (_openBlock(), _createBlock(_component_TablistRow, {
                          key: 0,
                          class: "font-bold text-gray-700 cursor-pointer",
                          onClick: _cache[3] || (_cache[3] = $event => (showAll.value.automations = true))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, [
                              _cache[15] || (_cache[15] = _createTextVNode(" Show more ", -1)),
                              _createVNode(_unref(ChevronDownIcon), { class: "h-6 w-6 inline" })
                            ])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          (!automations.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueWell, { key: 0 }, {
                default: _withCtx(() => [
                  _createVNode(_component_EmptyState, { plural: "upcoming automations" }, {
                    default: _withCtx(() => [
                      (canCreateMessages.value)
                        ? (_openBlock(), _createBlock(_component_router_link, {
                            key: 0,
                            to: "/automation/message/new"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_NeueButton, { type: "primary" }, {
                                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                  _createTextVNode("+ Message", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, _unref(activeTab) === 'upcoming']
        ])
      ])
    ]),
    _: 1
  }))
}
}

}